<template>
  <div id="SectionBookScopingCall">
    <UIContainer class="SectionBookScopingCall__container">
      <template #left>
        <div class="SectionBookScopingCall__card">
          <h3 class="SectionBookScopingCall__title">
            Book your free scoping call
          </h3>

          <UIForm
            class="SectionBookScopingCall__form"
            @submit.prevent="onSubmit"
          >
            <UIInputText
              name="fullName"
              label="Full name"
              isRequired
              placeholder="Full name"
            />

            <UIInputText
              name="companyName"
              label="Company name"
              isRequired
              placeholder="Company name"
            />

            <UIInputText
              name="email"
              label="Work email"
              type="email"
              isRequired
              placeholder="you@company.com"
            />

            <UIInputText
              name="phoneNumber"
              label="Phone number"
              type="tel"
              placeholder="+370"
            />

            <UIInputCheckbox
              name="agreeToPrivacy"
              isRequired
              label="By submitting, you agree to our Privacy Policy."
            />

            <UIButton
              type="submit"
              size="xl"
              theme="primary"
              :isLoading="isLoading"
              isFull
            >
              Book my free call
            </UIButton>
          </UIForm>
        </div>
      </template>

      <template #right>
        <img
          class="SectionBookScopingCall__image"
          src="../assets/bookScopingCall/photo.avif"
          alt="Person ready to help with your EcoVadis scoping call"
          loading="lazy"
          aria-label="Person ready to help with your EcoVadis scoping call"
        />
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIForm from '@/views/components/ui/forms/UIForm.vue'
import UIInputText from '@/views/components/ui/inputs/UIInputText.vue'
import UIInputCheckbox from '@/views/components/ui/inputs/UIInputCheckbox.vue'
import UIButton from '@/views/components/ui/buttons/UIButton.vue'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import { ref } from 'vue'
import { useToast } from '@/store/toast'
import { omnisend } from '@/lib/omnisend'

const { handleSubmit, resetForm } = useForm({
  validationSchema: yup.object({
    fullName: yup.string().required('Full name is required'),
    companyName: yup.string().required('Company name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phoneNumber: yup.string().optional(),
    agreeToPrivacy: yup
      .boolean()
      .required('You must agree to the privacy policy'),
  }),
})

const isLoading = ref(false)

const onSubmit = handleSubmit((values) => {
  if (isLoading.value) return

  isLoading.value = true
  omnisend
    .subscribe(
      {
        firstName: values.fullName,
        email: values.email,
        phone: values.phoneNumber,
        customProperties: {
          companyName: values.companyName,
          source: 'Ecovadis Book Scoping Call',
        },
      },
      ['ecovadis', 'bookScopingCall'],
    )
    .then(() => {
      useToast().onSuccess('Request submitted successfully')
      resetForm()
    })
    .finally(() => {
      isLoading.value = false
    })
})
</script>

<style lang="scss" scoped>
#SectionBookScopingCall {
  position: relative;
  background-color: $color-white;

  .SectionBookScopingCall__card {
    background: $color-white;
    border-radius: 1.25rem; // 20px
    box-shadow:
      0 32px 64px -12px rgba(0, 16, 53, 0.14),
      0 5px 5px -2.5px rgba(0, 16, 53, 0.04);
    padding: 2rem;
    max-width: 34rem;
    width: 100%;
  }

  :deep(.UIContainer__left) {
    padding-right: 0;
    @include md {
      padding-right: 2rem;
    }
  }

  .SectionBookScopingCall__title {
    @include font-size-36px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 2rem;
  }

  .SectionBookScopingCall__form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    @include md {
      gap: 1.5rem;
    }
  }

  .SectionBookScopingCall__image {
    display: none;

    @include md {
      display: block;
    }
  }
}
</style>
